import { useMemo } from 'react'
import { useAuth } from '@/context/AuthContext'
import { hasPermission, hasRole, isAdmin } from '@/components/common/PermissionGate'

export interface DashboardPermissions {
  // Feature access
  canViewDashboard: boolean
  canViewMasterReport: boolean
  canViewKPIs: boolean
  canViewCharts: boolean
  canViewUserManagement: boolean
  canViewRoleManagement: boolean
  canViewFeatureManagement: boolean
  canViewRowLevelSettings: boolean
  
  // Dashboard child permissions (individual components)
  canViewLeadsOverTime: boolean
  canViewGoalAchievement: boolean
  canViewMasterReportOverview: boolean
  canViewBudgetPacing: boolean
  canAccessBudgetPage: boolean
  
  // Action permissions
  canCreateUsers: boolean
  canEditUsers: boolean
  canDeleteUsers: boolean
  canAssignRoles: boolean
  canModifyPermissions: boolean
  
  // Role checks
  isAdmin: boolean
  isSuperAdmin: boolean
  isManager: boolean
  
  // Client access
  hasClientAccess: boolean
  clientAccessList: string[]
  
  // Helper functions
  hasFeatureAccess: (featureName: string, permission?: 'read' | 'write' | 'delete') => boolean
  hasRoleAccess: (roleName: string) => boolean
}

export function useDashboardPermissions(): DashboardPermissions {
  const { user } = useAuth()

  const permissions = useMemo(() => {
    if (!user) {
      return {
        canViewDashboard: false,
        canViewMasterReport: false,
        canViewKPIs: false,
        canViewCharts: false,
        canViewUserManagement: false,
        canViewRoleManagement: false,
        canViewFeatureManagement: false,
        canViewRowLevelSettings: false,
        canViewLeadsOverTime: false,
        canViewGoalAchievement: false,
        canViewMasterReportOverview: false,
        canViewBudgetPacing: false,
        canAccessBudgetPage: false,
        canCreateUsers: false,
        canEditUsers: false,
        canDeleteUsers: false,
        canAssignRoles: false,
        canModifyPermissions: false,
        isAdmin: false,
        isSuperAdmin: false,
        isManager: false,
        hasClientAccess: false,
        clientAccessList: [],
        hasFeatureAccess: () => false,
        hasRoleAccess: () => false,
      }
    }

    // Feature access checks - using correct feature names from database schema
    const hasDashboardParentAccess = hasPermission(user, 'Dashboard', 'read')
    
    // Dashboard child permissions (using correct feature names)
    const hasLeadsOverTimeAccess = hasPermission(user, 'Leads Over Time', 'read')
    const hasGoalAchievementAccess = hasPermission(user, 'Goal Achievement Summary', 'read')
    const hasMasterReportOverviewAccess = hasPermission(user, 'Master Report Overview', 'read')
    const hasBudgetPacingAccess = hasPermission(user, 'Budget Pacing', 'read')
    
    // Check if user has any admin features access
    const hasAdminFeatures = user.feature_access?.some(
      (fa) => fa.can_read && ["Users", "Roles", "Row Level Settings"].includes(fa.feature.name)
    ) || false
    
    // Dashboard route access logic:
    // 1. Parent "Dashboard" permission always grants base access
    // 2. Any dashboard child permission also grants route access 
    // 3. Admin features also grant dashboard access
    const hasBudgetPacingForRoute = hasBudgetPacingAccess || hasDashboardParentAccess
    const canViewDashboard = hasDashboardParentAccess || 
                             hasLeadsOverTimeAccess || 
                             hasGoalAchievementAccess || 
                             hasMasterReportOverviewAccess ||
                             hasBudgetPacingForRoute ||
                             hasAdminFeatures
    
    // Master Report (separate feature, not dashboard child)
    const canViewMasterReport = hasPermission(user, 'Master Report', 'read')
    
    // Individual component permissions within dashboard
    // KPIs (DashboardStats) is a general component that shows when:
    // - Dashboard group (parent) has access, OR
    // - Any of the dashboard child components (LOT/GAS/MR overview) have access
    const canViewKPIs = hasDashboardParentAccess || 
                        hasLeadsOverTimeAccess || 
                        hasGoalAchievementAccess || 
                        hasMasterReportOverviewAccess ||
                        hasBudgetPacingAccess
  const canViewBudgetPacingResolved = hasBudgetPacingAccess
    
    // Charts are shown based on specific permissions for components that display charts                    
    const canViewCharts = hasDashboardParentAccess || hasLeadsOverTimeAccess || hasGoalAchievementAccess || canViewBudgetPacingResolved
    const canViewUserManagement = hasPermission(user, 'Users', 'read')
    const canViewRoleManagement = hasPermission(user, 'Roles', 'read')
    const canViewFeatureManagement = hasPermission(user, 'Features', 'read')
    const canViewRowLevelSettings = hasPermission(user, 'Row Level Settings', 'read')

    // Action permissions
    const canCreateUsers = hasPermission(user, 'Users', 'write')
    const canEditUsers = hasPermission(user, 'Users', 'write')
    const canDeleteUsers = hasPermission(user, 'Users', 'delete')
    const canAssignRoles = hasPermission(user, 'Roles', 'write')
    const canModifyPermissions = hasPermission(user, 'Features', 'write')

    // Role checks
    const isUserAdmin = isAdmin(user)
    const isSuperAdmin = hasRole(user, 'super_admin')
    const isManager = hasRole(user, 'manager')

    // Client access
    const clientAccessList = user.account_access?.map(access => access.account_uid) || []
    const hasClientAccess = clientAccessList.length > 0 || isUserAdmin

    // Helper functions
    const hasFeatureAccess = (featureName: string, permission: 'read' | 'write' | 'delete' = 'read') => {
      return hasPermission(user, featureName, permission)
    }

    const hasRoleAccess = (roleName: string) => {
      return hasRole(user, roleName)
    }

    return {
      canViewDashboard,
      canViewMasterReport,
      canViewKPIs,
      canViewCharts,
      canViewUserManagement,
      canViewRoleManagement,
      canViewFeatureManagement,
      canViewRowLevelSettings,
      canViewLeadsOverTime: hasLeadsOverTimeAccess,
      canViewGoalAchievement: hasGoalAchievementAccess,
      canViewMasterReportOverview: hasMasterReportOverviewAccess,
      canViewBudgetPacing: canViewBudgetPacingResolved,
      canAccessBudgetPage: isUserAdmin || canViewBudgetPacingResolved,
      canCreateUsers,
      canEditUsers,
      canDeleteUsers,
      canAssignRoles,
      canModifyPermissions,
      isAdmin: isUserAdmin,
      isSuperAdmin,
      isManager,
      hasClientAccess,
      clientAccessList,
      hasFeatureAccess,
      hasRoleAccess,
    }
  }, [user])

  return permissions
}

// Helper hook for checking specific dashboard section permissions
export function useDashboardSectionPermissions(sectionName: string) {
  const { user } = useAuth()
  const permissions = useDashboardPermissions()

  const sectionPermissions = useMemo(() => {
    switch (sectionName.toLowerCase()) {
      case 'kpis':
      case 'dashboard_stats':
        return {
          canView: permissions.canViewKPIs,
          canEdit: permissions.hasFeatureAccess('Dashboard', 'write'),
          featureName: 'Dashboard'
        }
      
      case 'charts':
        return {
          canView: permissions.canViewCharts,
          canEdit: permissions.hasFeatureAccess('Dashboard', 'write'),
          featureName: 'Dashboard'
        }
      
      case 'leads_over_time':
        return {
          canView: permissions.canViewLeadsOverTime,
          canEdit: permissions.hasFeatureAccess('Leads Over Time', 'write'),
          featureName: 'Leads Over Time'
        }
        
      case 'goal_achievement':
      case 'goal_achievement_summary':
        return {
          canView: permissions.canViewGoalAchievement,
          canEdit: permissions.hasFeatureAccess('Goal Achievement Summary', 'write'),
          featureName: 'Goal Achievement Summary'
        }
      
      case 'master_report_overview':
        return {
          canView: permissions.canViewMasterReportOverview,
          canEdit: permissions.hasFeatureAccess('Master Report Overview', 'write'),
          featureName: 'Master Report Overview'
        }
      
      case 'budget_pacing':
      case 'budget pacing':
        return {
          canView: permissions.canViewBudgetPacing,
          canEdit: permissions.hasFeatureAccess('Budget Pacing', 'write'),
          featureName: 'Budget Pacing'
        }
      
      case 'master_report':
        return {
          canView: permissions.canViewMasterReport,
          canEdit: permissions.hasFeatureAccess('Master Report', 'write'),
          featureName: 'Master Report'
        }
      
      case 'user_management':
        return {
          canView: permissions.canViewUserManagement,
          canEdit: permissions.canEditUsers,
          featureName: 'Users'
        }
      
      default:
        return {
          canView: false,
          canEdit: false,
          featureName: ''
        }
    }
  }, [sectionName, permissions])

  return sectionPermissions
}

export default useDashboardPermissions 